// ScribeDashboard.jsx - Fixed version with proper notification handling
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminService } from "../../apis/adminService";
import { NotificationBadge } from "./NotificationBadge.jsx";
import { useNotifications } from '../../context/NotificationContext';
import { ToastNotification } from "./ToastNotification.jsx";

export function ScribeDashboard({ onStartEditing }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("All Time");
  const [loggedInUser, setLoggedInUser] = useState({ name: "Scribe" });
  const [stats, setStats] = useState({ total: 0, inProgress: 0, notStarted: 0 });
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

 // ---- Global notification state from context ----
const {
    notifications,           // live list of notifications
    unreadCount,             // live unread count
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();


  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.name) {
          setLoggedInUser({ 
            ...parsedUser,
            userId: parsedUser.userId || parsedUser.id // Make sure we have userId
          });
        }
      }
    } catch (err) {
      console.error("Failed to read user from localStorage", err);
    }
  }, []);

  // Fetch assigned sessions on component mount and when filters change
  useEffect(() => {
    fetchAssignedSessions();
  }, [statusFilter, dateFilter]);


// ---- Notification handlers now use context methods ----
  const handleNotificationClick = (notification) => {
    if (notification.relatedEntityType === "soap_note" && notification.relatedEntityId) {
      // Mark as read via context
      markAsRead(notification.notificationId);
      navigate(`/scribe?soapNoteId=${notification.relatedEntityId}&tab=soap-note&forceRefresh=${Date.now()}`, {
        state: { notificationSource: true, forceRefresh: true },
      });
    }
  };

  const fetchAssignedSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch scribe's assigned SOAP notes
      const response = await adminService.getMyAssignedSOAPNotes();

      if (response.success) {
        // Extract soap notes with multiple fallbacks
        let soapNotes = [];

        if (response.data?.data?.soapNotes) {
          soapNotes = response.data.data.soapNotes;
        } else if (response.data?.soapNotes) {
          soapNotes = response.data.soapNotes;
        } else if (Array.isArray(response.data)) {
          soapNotes = response.data;
        }

        const convertedSessions = soapNotes.map(convertSOAPNoteToSession);
        setSessions(convertedSessions);

        // Calculate stats using backend status values
        const total = soapNotes.length;
        const inProgress = soapNotes.filter((s) => {
          const status = s.status?.toLowerCase();
          return ["in_progress", "draft", "pending_review"].includes(status);
        }).length;
        const notStarted = soapNotes.filter((s) => {
          const status = s.status?.toLowerCase();
          return ["assigned"].includes(status);
        }).length;

        setStats({ total, inProgress, notStarted });
      } else {
        console.error("Failed to fetch assigned sessions:", response.message);
        setSessions([]);
        setStats({ total: 0, inProgress: 0, notStarted: 0 });
      }
    } catch (error) {
      console.error("Error fetching assigned sessions:", error);
      setError("Failed to load assigned sessions. Please try again.");
      setSessions([]);
      setStats({ total: 0, inProgress: 0, notStarted: 0 });
    } finally {
      setLoading(false);
    }
  };

  const convertSOAPNoteToSession = (soapNote) => {
    if (!soapNote) return null;

    // Extract patient name
    let patientName = "Unknown Patient";

    if (soapNote.patient) {
      if (typeof soapNote.patient === "string") {
        patientName = soapNote.patient;
      } else if (typeof soapNote.patient === "object") {
        const firstName =
          soapNote.patient.firstName || soapNote.patient.first_name || "";
        const lastName =
          soapNote.patient.lastName || soapNote.patient.last_name || "";
        if (firstName || lastName) {
          patientName = `${firstName} ${lastName}`.trim();
        }
      }
    }

    if (soapNote.patientName && soapNote.patientName !== "Unknown Patient") {
      patientName = soapNote.patientName;
    }

    // Format dates
    const assignedDate = soapNote.assignedAt
      ? new Date(soapNote.assignedAt).toISOString().split("T")[0]
      : null;

    const lastEdited =
      soapNote.updatedAt || soapNote.lastModified || soapNote.createdAt;
    const lastEditedFormatted = lastEdited
      ? new Date(lastEdited).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

    // Calculate due date (2 days from assignment)
    let dueDate = null;
    if (soapNote.assignedAt) {
      try {
        const date = new Date(soapNote.assignedAt);
        date.setDate(date.getDate() + 2);
        dueDate = date.toISOString().split("T")[0];
      } catch (e) {
        console.error("Error calculating due date:", e);
      }
    }

    // Get display status with proper mapping
    const getDisplayStatus = (backendStatus) => {
      if (!backendStatus) return "Unknown";

      const statusMap = {
        assigned: "Assigned",
        draft: "Draft Saved",
        in_progress: "In Progress",
        pending_review: "Pending Review",
        finalized: "Finalized",
        completed: "Finalized",
        signed: "Finalized",
        approved: "Finalized",
      };

      return statusMap[backendStatus.toLowerCase()] || backendStatus;
    };

    return {
      id: soapNote.soapNoteId || "",
      sessionId: soapNote.sessionId || "",
      patientId: soapNote.patient?.patientId || soapNote.patientId || "N/A",
      patientName,
      encounterType:
        soapNote.session?.sessionName || soapNote.encounterType || "N/A",
      soapVersion: soapNote.soapVersion
        ? soapNote.soapVersion.replace("v", "")
        : "1",
      status: getDisplayStatus(soapNote.status),
      backendStatus: soapNote.status,
      assignedDate,
      dueDate,
      lastEdited: lastEditedFormatted,
      rawData: soapNote,
    };
  };

  // Filter sessions by date and status
  const filteredSessions = sessions.filter((session) => {
    // Apply date filter
    let dateMatch = true;
    if (dateFilter === "Today") {
      const today = new Date().toISOString().slice(0, 10);
      dateMatch = session.assignedDate === today;
    } else if (dateFilter === "Last 7 Days") {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      dateMatch =
        session.assignedDate && new Date(session.assignedDate) >= weekAgo;
    } else if (dateFilter === "Last 30 Days") {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      dateMatch =
        session.assignedDate && new Date(session.assignedDate) >= monthAgo;
    }

    // Apply status filter
    if (statusFilter === "All") {
      return dateMatch;
    }

    // Map frontend status filter to backend status values
    const statusMap = {
      Assigned: ["assigned"],
      "In Progress": ["in_progress"],
      "Draft Saved": ["draft"],
      "Pending Review": ["pending_review"],
      Finalized: ["finalized", "completed", "signed", "approved"],
    };

    const backendStatuses = statusMap[statusFilter] || [];
    return (
      dateMatch &&
      backendStatuses.includes(session.backendStatus?.toLowerCase())
    );
  });

  const getStatusBadge = (status) => {
    if (!status) return "bg-gray-100 text-gray-800 border border-gray-200";

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "assigned":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "in progress":
      case "draft saved":
        return "bg-orange-100 text-orange-800 border border-orange-200";
      case "pending review":
      case "pending_review":
        return "bg-purple-100 text-purple-800 border border-purple-200";
      case "finalized":
      case "approved":
      case "signed":
        return "bg-green-100 text-green-800 border border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const redirectToSOAPEditor = (session) => {
  if (!session) return;

  // Prefer soapNoteId over sessionId for assigned notes
  const idPriority = [
    { value: session.id, type: 'soapNoteId' },
    { value: session.soapNoteId, type: 'soapNoteId' },
    { value: session.sessionId, type: 'sessionId' },
    { value: session.rawData?.soapNoteId, type: 'soapNoteId' },
    { value: session.rawData?.sessionId, type: 'sessionId' },
  ];

  const { value: targetId, type: idType } = 
    idPriority.find(({ value }) => Boolean(value)) || {};

  if (!targetId) {
    console.error("No valid ID found for redirection:", session);
    setToast({
      show: true,
      message: "Unable to open editor - missing session ID",
      type: "error",
    });
    return;
  }

  // For assigned SOAP notes, always use soapNoteId
  if (session.status === "Assigned" || session.backendStatus === "assigned") {
    navigate(`/scribe?soapNoteId=${targetId}&tab=soap-note&assigned=true`);
  } else {
    navigate(
      idType === 'soapNoteId'
        ? `/scribe?soapNoteId=${targetId}&tab=soap-note`
        : `/scribe?sessionId=${targetId}`
    );
  }
};


  // Fixed: Receive the full session object
  const handleStartEditing = (session) => {
    if (!session) return;
    // Store session data in localStorage or context for persistence
  sessionStorage.setItem('lastViewedSession', JSON.stringify(session));

    if (onStartEditing) {
      onStartEditing(session);
    } else {
      redirectToSOAPEditor(session);
    }
  };

  const handleRefresh = () => {
    fetchAssignedSessions();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      {toast.show && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header Added notificationCount prop */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Your Tasks
              </h1>
            </div>
            {/* Wrap notification and refresh in a flex container */}
            <div className="flex items-center gap-3">
             {/* NotificationBadge now receives data from context */}
             <NotificationBadge
                notifications={notifications}
                notificationCount={unreadCount}
                currentUser={loggedInUser}
                onMarkAsRead={markAsRead}
                onMarkAllAsRead={markAllAsRead}
                onClearNotification={deleteNotification}
                onNotificationClick={handleNotificationClick}
              />
              
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Total Assigned
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  In Progress
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.inProgress}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-50 flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-orange-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  Not Started
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.notStarted}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-yellow-50 flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section - Remains the same */}
        <div className="bg-white rounded-xl shadow mb-6 border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              My Assigned Charts
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>
                Showing {filteredSessions.length} of {sessions.length} charts
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option>All</option>
                <option>Assigned</option>
                <option>In Progress</option>
                <option>Draft Saved</option>
                <option>Pending Review</option>
                <option>Finalized</option>
              </select>
            </div>

            {/* Date Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Assigned Date
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option>All Time</option>
                <option>Today</option>
                <option>Last 7 Days</option>
                <option>Last 30 Days</option>
              </select>
            </div>
          </div>

          {/* Reset Button */}
          {(statusFilter !== "All" || dateFilter !== "All Time") && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setStatusFilter("All");
                  setDateFilter("All Time");
                }}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                Reset filters
              </button>
            </div>
          )}
        </div>

        {/* Charts Table*/}
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Date
                  </th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                        <div className="text-gray-500">
                          Loading assigned charts...
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : filteredSessions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center">
                      <div className="text-gray-500 mb-2">
                        {sessions.length === 0
                          ? "No charts assigned to you yet"
                          : "No charts match the current filters"}
                      </div>
                      {sessions.length > 0 && (
                        <button
                          onClick={() => {
                            setStatusFilter("All");
                            setDateFilter("All Time");
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          Reset filters to show all charts
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredSessions.map((session) => (
                    <tr
                      key={session.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="p-4">
                        <div className="text-sm font-medium text-gray-900">
                          {session.patientName}
                        </div>
                        {session.patientId && (
                          <div className="text-xs text-gray-500 mt-1">
                            ID: {session.patientId}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                            session.status
                          )}`}
                        >
                          {session.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-gray-700">
                          {session.assignedDate
                            ? new Date(session.assignedDate).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )
                            : "-"}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-gray-600">
                          {session.lastEdited}
                        </div>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => handleStartEditing(session)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            session.status === "Assigned"
                              ? "bg-blue-600 text-white hover:bg-blue-700"
                              : "bg-orange-600 text-white hover:bg-orange-700"
                          }`}
                        >
                          {session.status === "Assigned"
                            ? "Start"
                            : session.status === "Finalized"
                            ? "View"
                            : "Continue"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Footer */}
        <div className="mt-6 bg-white rounded-xl shadow border border-gray-200 p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-blue-600"></div>
              <span className="text-gray-600">Total Assigned:</span>
              <span className="font-medium text-gray-900 ml-1">
                {stats.total}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-orange-600"></div>
              <span className="text-gray-600">In Progress:</span>
              <span className="font-medium text-gray-900 ml-1">
                {stats.inProgress}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-yellow-600"></div>
              <span className="text-gray-600">Not Started:</span>
              <span className="font-medium text-gray-900 ml-1">
                {stats.notStarted}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
